import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Lock, Shield, Phone } from "lucide-react";
import { Button } from "../ui/button";
import { fadeIn, fadeInUp, fadeInUpSmall } from "../../lib/animations";

const plans = [
  {
    id: "solo_agent",
    name: "Solo Agent",
    bestFor: "Individual agents getting started",
    monthlyPrice: 199,
    foundingMonthly: 149,
    features: [
      "MLS Status Sync",
      "URL-to-Ad Engine",
      "Weekly Seller Reports",
      "Up to 10 active campaigns",
      "AI-generated ad creatives",
      "Fair Housing compliance",
      "Email support",
    ],
    cta: "Get Early Access",
    popular: false,
  },
  {
    id: "market_leader",
    name: "Market Leader",
    bestFor: "Serious agents who want to scale",
    monthlyPrice: 499,
    foundingMonthly: 399,
    features: [
      "Everything in Solo Agent",
      "Direct Meta Deployment",
      "AI Voice Nurture (Vapi.ai)",
      "AI Creative Lab",
      "DIVA Video Ads",
      "Two-Way CRM Sync",
      "Unlimited campaigns",
      "Priority support",
    ],
    cta: "Get Founding Member Access",
    popular: true,
    trial: true,
  },
  {
    id: "brokerage_moat",
    name: "Brokerage Moat",
    bestFor: "Brokerages and teams",
    monthlyPrice: 1499,
    foundingMonthly: 999,
    features: [
      "Everything in Market Leader",
      "Multi-agent portal",
      "Master View Dashboard",
      "White-Label Reports",
      "Custom integrations",
      "Dedicated account support",
    ],
    cta: "Get Early Access",
    popular: false,
  },
];

const ANNUAL_DISCOUNT = 0.2;

export function InlinePricingSection({ foundingStatus, navigate }) {
  const [billing, setBilling] = useState("monthly");
  const isAnnual = billing === "annual";
  const isFoundingActive = foundingStatus?.is_active;

  const getPrice = (plan) => {
    const base = isFoundingActive ? plan.foundingMonthly : plan.monthlyPrice;
    return isAnnual ? Math.round(base * (1 - ANNUAL_DISCOUNT)) : base;
  };

  const getOriginalPrice = (plan) => plan.monthlyPrice;

  const getAnnualSavings = (plan) => {
    const base = isFoundingActive ? plan.foundingMonthly : plan.monthlyPrice;
    return Math.round(base * ANNUAL_DISCOUNT * 12);
  };

  const handleCTA = (plan) => {
    if (plan.bookCall) {
      navigate('/contact');
    } else {
      navigate('/pricing');
    }
  };

  return (
    <section id="pricing" className="relative z-10 px-6 md:px-12 lg:px-24 py-20 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={fadeInUpSmall.initial}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.05 }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Simple, Transparent Pricing</h2>
          <p className="text-gray-600 text-base md:text-lg max-w-xl mx-auto">
            Choose the plan that fits your business. No hidden fees. Cancel anytime.
          </p>
        </motion.div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center mb-10" data-testid="billing-toggle">
          <div className="inline-flex items-center bg-gray-200 rounded-full p-1">
            <button
              onClick={() => setBilling("monthly")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                !isAnnual ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
              data-testid="billing-monthly-btn"
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling("annual")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
                isAnnual ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
              data-testid="billing-annual-btn"
            >
              Annual
              <span className="text-[10px] font-bold bg-[#39FF14] text-black px-1.5 py-0.5 rounded-full">
                -20%
              </span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan, i) => {
            const displayPrice = getPrice(plan);
            const originalMonthly = getOriginalPrice(plan);
            const annualSavings = getAnnualSavings(plan);

            return (
              <motion.div
                key={plan.id}
                initial={fadeInUpSmall.initial}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.05 }}
                transition={{ delay: i * 0.08 }}
                className={`relative bg-white rounded-2xl p-6 border-2 transition-all ${
                  plan.popular
                    ? 'border-[#39FF14] shadow-lg shadow-[#39FF14]/10'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid={`pricing-card-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="bg-[#39FF14] text-black text-xs font-semibold px-4 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="mb-5">
                  <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">Best for: {plan.bestFor}</p>
                </div>

                <div className="mb-5">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-gray-900">${displayPrice}</span>
                    <span className="text-gray-500">/mo</span>
                  </div>

                  {/* Founding member savings */}
                  {isFoundingActive && !isAnnual && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-gray-400 line-through">${originalMonthly}/mo</span>
                      <span className="text-xs font-medium text-[#39FF14] bg-[#39FF14]/10 px-2 py-0.5 rounded-full">
                        Save ${originalMonthly - displayPrice}/mo
                      </span>
                    </div>
                  )}

                  {/* Annual savings */}
                  {isAnnual && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-gray-400 line-through">
                        ${isFoundingActive ? plan.foundingMonthly : originalMonthly}/mo
                      </span>
                      <span className="text-xs font-medium text-[#39FF14] bg-[#39FF14]/10 px-2 py-0.5 rounded-full">
                        Save ${annualSavings.toLocaleString()}/yr
                      </span>
                    </div>
                  )}

                  {isFoundingActive && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                      <Lock className="w-3 h-3" />
                      <span>Founding Member price — locked for life</span>
                    </div>
                  )}

                  {isAnnual && (
                    <p className="text-xs text-gray-500 mt-1">Billed annually</p>
                  )}
                </div>

                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <Check className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleCTA(plan)}
                  className={`w-full py-3 h-auto font-semibold text-sm ${
                    plan.popular
                      ? 'bg-[#39FF14] text-black hover:bg-[#32E012]'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                  data-testid={`pricing-cta-${plan.id}`}
                >
                  {plan.bookCall ? (
                    <><Phone className="w-4 h-4 mr-1.5" /> {plan.cta}</>
                  ) : (
                    <>{plan.cta} <ArrowRight className="w-4 h-4 ml-1 flex-shrink-0" /></>
                  )}
                </Button>

                {plan.trial && (
                  <p className="text-center text-xs text-gray-500 mt-3">7-day free trial included</p>
                )}
              </motion.div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-10 text-sm text-gray-500">
          <div className="flex items-center gap-1.5">
            <Shield className="w-4 h-4 text-gray-400" />
            <span>Cancel anytime</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Lock className="w-4 h-4 text-gray-400" />
            <span>No long-term contracts</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Check className="w-4 h-4 text-gray-400" />
            <span>Secure payment via Stripe</span>
          </div>
        </div>
      </div>
    </section>
  );
}
